import { computed, watch, onMounted, onUnmounted, ref } from 'vue'
import { useWebSocketStore, type CallStartedEvent, type CallEndedEvent, type OperatorStatusChangedEvent, type CampaignStatsUpdatedEvent, type NewLeadEvent, type LeadUpdatedEvent, type RecordingCreatedEvent, type RecordingUpdatedEvent } from '@/stores/websocket'

export interface AbsenceCheckEvent {
  operator_id: number
  absence_asked_at: string
}

export type EventType = 'call_started' | 'call_ended' | 'operator_status_changed' | 'campaign_stats_updated' | 'new_lead' | 'lead_updated' | 'recording_created' | 'recording_updated' | 'absence_check'

interface EventSubscription {
  type: EventType
  handlerId: number
}

let handlerIdCounter = 0

function useWebSocketEventQueue() {
  const wsStore = useWebSocketStore()

  // Un cursor de "último procesado" POR TIPO de evento. Un único cursor global
  // haría que un handler de un tipo consumiera (marcara como procesados) eventos
  // de otros tipos que aún no se han procesado, perdiéndolos.
  const lastProcessedByType = ref<Record<string, number>>({})

  const pendingEventsByType = (eventType: EventType) => computed(() => {
    const since = lastProcessedByType.value[eventType] ?? 0
    return wsStore.events
      .filter((queued) => queued.id > since && queued.event.event === eventType)
      .map((queued) => queued.event.data)
  })

  const pendingQueuedByType = (eventType: EventType) => computed(() => {
    const since = lastProcessedByType.value[eventType] ?? 0
    return wsStore.events.filter((queued) => queued.id > since && queued.event.event === eventType)
  })

  const markProcessedUpTo = (eventType: EventType, id: number) => {
    const current = lastProcessedByType.value[eventType] ?? 0
    if (id > current) {
      lastProcessedByType.value = { ...lastProcessedByType.value, [eventType]: id }
    }
  }

  return { wsStore, pendingEventsByType, pendingQueuedByType, markProcessedUpTo }
}

export function useRealTimeUpdates() {
  const { wsStore, pendingEventsByType, pendingQueuedByType, markProcessedUpTo } = useWebSocketEventQueue()
  const subscriptions = ref(new Map<number, EventSubscription>())

  const callStartedEvents = pendingEventsByType('call_started')
  const callEndedEvents = pendingEventsByType('call_ended')
  const operatorStatusChangedEvents = pendingEventsByType('operator_status_changed')
  const campaignStatsUpdatedEvents = pendingEventsByType('campaign_stats_updated')
  const newLeadEvents = pendingEventsByType('new_lead')
  const leadUpdatedEvents = pendingEventsByType('lead_updated')
  const recordingCreatedEvents = pendingEventsByType('recording_created')
  const recordingUpdatedEvents = pendingEventsByType('recording_updated')
  const absenceCheckEvents = pendingEventsByType('absence_check')

  const connect = () => {
    wsStore.connect()
  }

  const disconnect = () => {
    wsStore.disconnect()
  }

  const subscribeToChannel = (channel: string) => {
    wsStore.subscribe(channel)
  }

  const unsubscribeFromChannel = (channel: string) => {
    wsStore.unsubscribe(channel)
  }

  const registerHandler = (type: EventType, callback: (data: any) => void, filter?: (data: any) => boolean) => {
    const handlerId = ++handlerIdCounter
    const eventQueue = computed(() => {
      switch (type) {
        case 'call_started': return callStartedEvents.value
        case 'call_ended': return callEndedEvents.value
        case 'operator_status_changed': return operatorStatusChangedEvents.value
        case 'campaign_stats_updated': return campaignStatsUpdatedEvents.value
        case 'new_lead': return newLeadEvents.value
        case 'lead_updated': return leadUpdatedEvents.value
        case 'recording_created': return recordingCreatedEvents.value
        case 'recording_updated': return recordingUpdatedEvents.value
        case 'absence_check': return absenceCheckEvents.value
      }
      return []
    })

    const stop = watch(eventQueue, (events) => {
      if (!events || events.length === 0) return
      events.forEach((event) => {
        if (filter && !filter(event)) return
        callback(event)
      })
      // Marcar como procesado SOLO el último evento de ESTE tipo, no el último
      // global de la cola (que podría ser de otro tipo y aún no procesado).
      const queued = pendingQueuedByType(type).value
      const lastOfType = queued[queued.length - 1]
      if (lastOfType) {
        markProcessedUpTo(type, lastOfType.id)
      }
    }, { immediate: false, flush: 'sync' })

    subscriptions.value.set(handlerId, { type, handlerId })

    return () => {
      stop()
      subscriptions.value.delete(handlerId)
    }
  }

  const onCallStarted = (callback: (data: CallStartedEvent) => void) => registerHandler('call_started', callback)
  const onCallEnded = (callback: (data: CallEndedEvent) => void) => registerHandler('call_ended', callback)
  const onOperatorStatusChanged = (callback: (data: OperatorStatusChangedEvent) => void) => registerHandler('operator_status_changed', callback)
  const onCampaignStatsUpdated = (callback: (data: CampaignStatsUpdatedEvent) => void) => registerHandler('campaign_stats_updated', callback)
  const onNewLead = (callback: (data: NewLeadEvent) => void) => registerHandler('new_lead', callback)
  const onLeadUpdated = (callback: (data: LeadUpdatedEvent) => void) => registerHandler('lead_updated', callback)
  const onRecordingCreated = (callback: (data: RecordingCreatedEvent) => void) => registerHandler('recording_created', callback)
  const onRecordingUpdated = (callback: (data: RecordingUpdatedEvent) => void) => registerHandler('recording_updated', callback)
  const onAbsenceCheck = (callback: (data: AbsenceCheckEvent) => void) => registerHandler('absence_check', callback)

  return {
    wsStore,
    connectionStatus: computed(() => wsStore.connectionStatus),
    isConnected: computed(() => wsStore.isConnected),
    isConnecting: computed(() => wsStore.isConnecting),
    lastEvent: computed(() => wsStore.lastEvent),
    callStartedEvents,
    callEndedEvents,
    operatorStatusChanged: operatorStatusChangedEvents,
    campaignStatsUpdated: campaignStatsUpdatedEvents,
    newLeadEvent: newLeadEvents,
    leadUpdatedEvent: leadUpdatedEvents,
    recordingCreatedEvent: recordingCreatedEvents,
    recordingUpdatedEvent: recordingUpdatedEvents,
    absenceCheckEvent: absenceCheckEvents,
    connect,
    disconnect,
    subscribeToChannel,
    unsubscribeFromChannel,
    onCallStarted,
    onCallEnded,
    onOperatorStatusChanged,
    onCampaignStatsUpdated,
    onNewLead,
    onLeadUpdated,
    onRecordingCreated,
    onRecordingUpdated,
    onAbsenceCheck,
  }
}

export function useOperatorRealTimeUpdates(operatorId: number) {
  const { wsStore, connect } = useRealTimeUpdates()

  onMounted(() => {
    connect()
    wsStore.subscribe(`operator:${operatorId}`)
  })

  onUnmounted(() => {
    wsStore.unsubscribe(`operator:${operatorId}`)
  })

  return {
    ...useRealTimeUpdates(),
  }
}

export function useCampaignRealTimeUpdates(campaignId: number) {
  const { wsStore, connect } = useRealTimeUpdates()

  onMounted(() => {
    connect()
    wsStore.subscribe(`campaign:${campaignId}`)
  })

  onUnmounted(() => {
    wsStore.unsubscribe(`campaign:${campaignId}`)
  })

  return {
    ...useRealTimeUpdates(),
  }
}

export function useAllRealTimeUpdates() {
  const { wsStore, connect } = useRealTimeUpdates()

  onMounted(() => {
    connect()
    wsStore.subscribe('all')
  })

  onUnmounted(() => {
    wsStore.unsubscribe('all')
  })

  return {
    ...useRealTimeUpdates(),
  }
}

export function useGeneratorRealTimeUpdates(userId: number) {
  const { wsStore, connect, onNewLead } = useRealTimeUpdates()

  onMounted(() => {
    connect()
    wsStore.subscribe('all')
  })

  onUnmounted(() => {
    wsStore.unsubscribe('all')
  })

  const onMyNewLead = (callback: (data: NewLeadEvent) => void) => {
    return onNewLead((data) => {
      if (data.generator_id === userId) {
        callback(data)
      }
    })
  }

  const onMyLeadUpdated = (callback: (data: LeadUpdatedEvent) => void) => {
    const { onLeadUpdated } = useRealTimeUpdates()
    return onLeadUpdated((data) => {
      if (data.generator_id === userId) {
        callback(data)
      }
    })
  }

  return {
    ...useRealTimeUpdates(),
    onMyNewLead,
    onMyLeadUpdated,
  }
}
